import React, { useState, useRef } from 'react'
import {
  Grid,
  Typography,
  Button,
  Snackbar,
  Container,
  Alert as MuiAlert,
} from '@mui/material'
import { makeStyles, createStyles } from '@mui/styles'
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import { getURLSearchParams } from '../../windowManager'
import WorkOrderlanding from '../WorkOrderLanding/WorkOrderLanding'
import {
  WORK_ORDER_NUMBER_FIELD,
  WORK_ORDER_ID_FIELD,
  HOW_TO_UPLOAD_IMAGES,
  UPLOAD_IMAGES,
  SELECT_IMAGES,
  UPLOAD_IMAGE_COUNT_WARNING,
  UPLOAD_IMAGE_TYPE_WARNING,
  api,
  LOADING_MESSAGE,
  UPLOADED_SUCCESSFULLY_MESSAGE,
  UPLOADED_ERROR_MESSAGE,
  RETRY_UPLOAD,
} from '../../globalConstants'
import WorkOrderImageList from '../WorkOrderImageList/WorkOrderImageList'
import { validateImgFile, sanitizeFileName } from '../../utils'
import { postDataWithCb } from '../../service/HttpService'
import { logWorkOrderAppEvent } from '../../service/WorkOrderAppLoggingService'
import { createTcsErrorLogger } from '../../service/TgtLoggerService'
import { useAuth } from '@praxis/component-auth'
import useGetWorkOrderDetails from '../../utils/hooks/useGetWorkOrderDetails'
import imageCompression from 'browser-image-compression'

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      padding: theme.spacing(1, 0, 0, 2),
    },
    iconFontSize: {
      fontSize: '2rem',
    },
    uploadButtonDiv: {
      marginLeft: 'auto',
      display: 'flex',
      gap: theme.spacing(1),
    },
    photoUploadInput: {
      display: 'none',
    },
  }),
)

const UPLOAD_PHOTO_TS_FILE = 'src/components/UploadPhoto/UploadPhoto.js'
const MAX_UPLOAD_FILE_COUNT = 5

const getFileMetadata = (file, index) => ({
  index,
  name: file?.name || file?.title || '',
  description: file?.description || '',
  loading: file?.loading || false,
  size: file?.size || file?.fileData?.size || '',
  type: file?.type || file?.fileData?.type || '',
})

const getFilesMetadata = (files = []) =>
  Array.from(files).map((file, index) => getFileMetadata(file, index))

const isString = (value) => typeof value === 'string'

export default function UploadPhoto() {
  const classes = useStyles()
  const auth = useAuth()
  const woDetailFetched = useGetWorkOrderDetails()
  const [fileCountError, setFileCountError] = useState(false)
  const [fileTypeError, setFileTypeError] = useState(false)
  const [uploadedSnackbar, setUploadedSnackbar] = useState(false)
  const [errorSnackbar, setErrorSnackbar] = useState(false)
  const [imagesToUpload, setImagesToUpload] = useState([])
  const woNumber = getURLSearchParams().get(WORK_ORDER_NUMBER_FIELD)
  const woId = getURLSearchParams().get(WORK_ORDER_ID_FIELD)
  const inputFile = useRef(null)
  const cameraInputFile = useRef(null)
  const userId = auth.session.userInfo?.lanId
  const [uploaded, setUploaded] = useState(false)
  const hasUploadErrorImages = imagesToUpload?.filter(
    (val) => val.loading === 'error',
  )?.length
  const totalImages = imagesToUpload?.length
  const logUploadPhotoTcsError = createTcsErrorLogger({
    getBaseContext: () => ({
      userId: userId || 'unknown',
      woNumber,
      woId,
    }),
  })

  const logUploadPhotoEvent = ({
    method,
    params,
    maximoUrl,
    httpMethod,
    body,
    response,
    miscLogs,
  }) => {
    void logWorkOrderAppEvent({
      userId,
      method,
      tsFile: UPLOAD_PHOTO_TS_FILE,
      maximoUrl: maximoUrl ?? api.uploadImage,
      httpMethod: httpMethod ?? 'CLIENT',
      params: {
        woNumber,
        woId,
        userId: userId || 'user',
        pathname: window?.location?.pathname,
        search: window?.location?.search,
        hash: window?.location?.hash,
        uploadedState: uploaded,
        queuedImageCount: imagesToUpload?.length,
        uploadErrorImageCount: hasUploadErrorImages || 0,
        ...params,
      },
      body,
      response,
      miscLogs,
    })
  }

  const showUploadErrorWithLog = ({
    method,
    httpMethod,
    params,
    body,
    error,
    responseStatus,
    miscLogs,
  }) => {
    logUploadPhotoEvent({
      method,
      httpMethod,
      params,
      body,
      response: {
        status: responseStatus,
        error,
      },
      miscLogs,
    })
    logUploadPhotoTcsError({
      message: `${method} failed`,
      method,
      error,
      nonIndexedFields: {
        source: params?.source || 'unknown',
        fileIndex: Number.isInteger(params?.fileIndex)
          ? params.fileIndex
          : 'n/a',
        responseStatus,
      },
    })
    setErrorSnackbar(true)
  }

  function onUploadPhotoClick() {
    inputFile.current.click()
  }

  function onCameraClick() {
    cameraInputFile.current.click()
  }

  async function handleFileUpload(e, source) {
    const files = e?.target?.files ?? []
    const fileLength = files?.length
    if (!fileLength) {
      logUploadPhotoEvent({
        method: 'handleFileUpload',
        params: {
          source,
          selectedFileCount: 0,
        },
        response: {
          status: 'no_files_selected',
        },
        miscLogs: {
          message: 'Upload flow was opened but no files were selected.',
        },
      })

      return
    }

    // throw file count error if the number of images selected is more than 5 (it can also be the sum of already seleted images and the current selected images)
    if (
      fileLength > MAX_UPLOAD_FILE_COUNT ||
      (uploaded !== 'loaded' &&
        imagesToUpload.length + fileLength > MAX_UPLOAD_FILE_COUNT)
    ) {
      setFileCountError(true)
      logUploadPhotoEvent({
        method: 'handleFileUpload',
        params: {
          source,
          selectedFileCount: fileLength,
          maxAllowed: MAX_UPLOAD_FILE_COUNT,
          existingQueuedImages: imagesToUpload.length,
        },
        body: {
          selectedFiles: getFilesMetadata(files),
        },
        response: {
          status: 'validation_failed',
          reason: 'max_file_count_exceeded',
        },
        miscLogs: {
          message: 'Selected image count exceeded the upload limit.',
        },
      })
    } else {
      if (!validateImgFile(files)) {
        setFileTypeError(true)
        logUploadPhotoEvent({
          method: 'handleFileUpload',
          params: {
            source,
            selectedFileCount: fileLength,
          },
          body: {
            selectedFiles: getFilesMetadata(files),
          },
          response: {
            status: 'validation_failed',
            reason: 'invalid_file_type',
          },
          miscLogs: {
            message:
              'One or more selected files do not have a supported image extension.',
          },
        })
        return
      }
      let shouldResetUploadState = false

      // Clear any uploaded images , if exists
      if (imagesToUpload?.length > 0 && fileLength > 0) {
        if (uploaded === 'loaded') {
          shouldResetUploadState = true
          logUploadPhotoEvent({
            method: 'handleFileUpload',
            params: {
              source,
              selectedFileCount: fileLength,
            },
            response: {
              status: 'reset_after_successful_upload',
            },
            miscLogs: {
              previousImages: getFilesMetadata(imagesToUpload),
            },
          })
          setImagesToUpload([])
          setUploaded(false)
        } else if (uploaded === 'error') {
          shouldResetUploadState = true
          logUploadPhotoEvent({
            method: 'handleFileUpload',
            params: {
              source,
              selectedFileCount: fileLength,
            },
            response: {
              status: 'reset_after_failed_upload',
            },
            miscLogs: {
              previousImages: getFilesMetadata(imagesToUpload),
            },
          })
          setImagesToUpload(
            imagesToUpload.filter((val) => val.loading !== 'loaded'),
          )
          setUploaded(false)
        }
      }
      const imgArray = Array.from(files).map((file) => ({
        fileData: file,
        imgUrl: URL.createObjectURL(file),
        title: sanitizeFileName(file.name),
        loading: false,
        description: sanitizeFileName(file.name).replace(/\.[^/.]+$/, ''),
      }))
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      }
      logUploadPhotoEvent({
        method: 'handleFileUpload',
        params: {
          source,
          selectedFileCount: fileLength,
        },
        body: {
          selectedFiles: getFilesMetadata(files),
        },
        response: {
          status: 'files_accepted_for_processing',
        },
        miscLogs: {
          compressionOptions: options,
        },
      })
      const processedImages = await Promise.all(
        imgArray.map(async (file, index) => {
          let compressedFile

          try {
            compressedFile = await imageCompression(file.fileData, options)
          } catch (error) {
            showUploadErrorWithLog({
              method: 'handleFileUpload:imageCompression',
              params: {
                source,
                fileIndex: index,
              },
              body: {
                file: getFileMetadata(file, index),
              },
              error,
              responseStatus: 'image_compression_failed',
              miscLogs: {
                compressionOptions: options,
              },
            })

            return null
          }

          try {
            const src = await getImgSrc(compressedFile, index, imgArray, source)

            return {
              ...file,
              src,
            }
          } catch (error) {
            showUploadErrorWithLog({
              method: 'handleFileUpload:getImgSrc',
              params: {
                source,
                fileIndex: index,
              },
              body: {
                file: getFileMetadata(file, index),
                compressedFile: getFileMetadata(compressedFile, index),
              },
              error,
              responseStatus: 'image_preparation_failed',
            })
            return null
          }
        }),
      )

      const validImages = processedImages.filter(Boolean)

      if (validImages.length > 0) {
        setImagesToUpload((currentImages) =>
          (shouldResetUploadState ? [] : currentImages).concat(validImages),
        )
      }
    }
  }

  async function uploadImagesToServer() {
    setUploaded('start')
    const pendingImages = imagesToUpload.filter(
      (val) => val.loading !== 'loaded',
    )
    let uploadedCount = 0
    let failedImage = null

    if (!pendingImages.length) {
      setUploaded(false)
      logUploadPhotoEvent({
        method: 'uploadImagesToServer',
        httpMethod: 'POST',
        params: {
          pendingImageCount: 0,
          isRetry: false,
        },
        response: {
          status: 'upload_skipped_no_pending_images',
        },
      })

      return
    }

    try {
      const imgUploadDataArray = pendingImages // filter out already uploaded files - during error condition
        .map((img) => {
          return {
            url: api.uploadImage,
            body: JSON.stringify({
              woid: woId,
              attachment: img.src,
              name: img.title,
              description: img.description,
              userid: userId,
            }),
          }
        })
      logUploadPhotoEvent({
        method: 'uploadImagesToServer',
        httpMethod: 'POST',
        params: {
          pendingImageCount: pendingImages.length,
          isRetry: Boolean(hasUploadErrorImages),
        },
        body: {
          images: getFilesMetadata(pendingImages),
        },
        response: {
          status: 'upload_started',
        },
        miscLogs: {
          requestCount: imgUploadDataArray.length,
        },
      })
      setImagesToUpload(
        pendingImages // clear already uploaded files - during error condition
          .map((val) => ({
            ...val,
            loading: true,
          })),
      )
      await postDataWithCb(
        imgUploadDataArray,
        (uploadedIndex, error, uploadResponse) => {
          const image = pendingImages[uploadedIndex]

          if (error) {
            failedImage = getFileMetadata(image, uploadedIndex)
            logUploadPhotoEvent({
              method: 'uploadImagesToServer:image',
              httpMethod: 'POST',
              params: {
                uploadedIndex,
                isRetry: Boolean(hasUploadErrorImages),
              },
              body: {
                image: getFileMetadata(image, uploadedIndex),
              },
              response: {
                status: 'upload_failed',
                error,
              },
              miscLogs: {
                uploadedCount,
                pendingImageCount: pendingImages.length,
              },
            })
            logUploadPhotoTcsError({
              message: 'uploadImagesToServer:image failed',
              method: 'uploadImagesToServer:image',
              error,
              nonIndexedFields: {
                fileIndex: uploadedIndex,
                responseStatus: 'upload_failed',
                imageName: image?.title,
                pendingImageCount: pendingImages.length,
              },
            })
            setImagesToUpload((imagesToUpload) =>
              imagesToUpload.map((val, idx) => ({
                ...val,
                loading: uploadedIndex === idx ? 'error' : val.loading,
              })),
            )
            throw error
          } else {
            uploadedCount += 1
            logUploadPhotoEvent({
              method: 'uploadImagesToServer:image',
              httpMethod: 'POST',
              params: {
                uploadedIndex,
                isRetry: Boolean(hasUploadErrorImages),
              },
              body: {
                image: getFileMetadata(image, uploadedIndex),
              },
              response: {
                status: 'upload_succeeded',
                uploadResponse,
              },
              miscLogs: {
                uploadedCount,
                pendingImageCount: pendingImages.length,
              },
            })
            setImagesToUpload((imagesToUpload) =>
              imagesToUpload.map((val, idx) => ({
                ...val,
                loading: uploadedIndex === idx ? 'loaded' : val.loading,
              })),
            )
          }
        },
      )
      setUploaded('loaded')
      setUploadedSnackbar(true)
      logUploadPhotoEvent({
        method: 'uploadImagesToServer',
        httpMethod: 'POST',
        params: {
          pendingImageCount: pendingImages.length,
          uploadedCount,
          isRetry: Boolean(hasUploadErrorImages),
        },
        body: {
          images: getFilesMetadata(pendingImages),
        },
        response: {
          status: 'upload_complete_success',
        },
      })
    } catch (e) {
      setUploaded('error')
      showUploadErrorWithLog({
        method: 'uploadImagesToServer',
        httpMethod: 'POST',
        params: {
          pendingImageCount: pendingImages.length,
          uploadedCount,
          isRetry: Boolean(hasUploadErrorImages),
        },
        body: {
          images: getFilesMetadata(pendingImages),
        },
        error: e,
        responseStatus: 'upload_complete_error',
        miscLogs: {
          failedImage,
          remainingImageCount: pendingImages.length - uploadedCount,
        },
      })
    }
  }

  function getImgSrc(fileBlob, index, imgArray, source) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onerror = () => {
        logUploadPhotoEvent({
          method: 'getImgSrc',
          params: {
            source,
            fileIndex: index,
          },
          body: {
            file: getFileMetadata(imgArray[index], index),
            compressedFile: getFileMetadata(fileBlob, index),
          },
          response: {
            status: 'base64_conversion_failed',
            error: reader.error,
          },
        })
        logUploadPhotoTcsError({
          message: 'getImgSrc FileReader error',
          method: 'getImgSrc',
          error: reader.error,
          nonIndexedFields: {
            source,
            fileIndex: index,
            responseStatus: 'base64_conversion_failed',
            fileName: imgArray[index]?.title,
          },
        })

        reject(reader.error)
      }

      reader.onloadend = () => {
        if (!isString(reader.result)) {
          const error = new Error(
            'FileReader result was empty while converting image to base64.',
          )

          logUploadPhotoEvent({
            method: 'getImgSrc',
            params: {
              source,
              fileIndex: index,
            },
            body: {
              file: getFileMetadata(imgArray[index], index),
              compressedFile: getFileMetadata(fileBlob, index),
            },
            response: {
              status: 'base64_conversion_failed',
              error,
            },
          })
          logUploadPhotoTcsError({
            message: 'getImgSrc returned non-string result',
            method: 'getImgSrc',
            error,
            nonIndexedFields: {
              source,
              fileIndex: index,
              responseStatus: 'base64_conversion_failed',
              fileName: imgArray[index]?.title,
            },
          })

          reject(error)
          return
        }

        const base64String = reader.result.replace(
          /^data:image\/[a-z]+;base64,/,
          '',
        )

        logUploadPhotoEvent({
          method: 'getImgSrc',
          params: {
            source,
            fileIndex: index,
          },
          body: {
            file: getFileMetadata(imgArray[index], index),
            compressedFile: getFileMetadata(fileBlob, index),
          },
          response: {
            status: 'image_ready_for_upload',
          },
        })

        resolve(base64String)
      }

      try {
        reader.readAsDataURL(fileBlob)
      } catch (error) {
        logUploadPhotoEvent({
          method: 'getImgSrc',
          params: {
            source,
            fileIndex: index,
          },
          body: {
            file: getFileMetadata(imgArray[index], index),
            compressedFile: getFileMetadata(fileBlob, index),
          },
          response: {
            status: 'base64_conversion_failed_sync',
            error,
          },
        })
        logUploadPhotoTcsError({
          message: 'getImgSrc readAsDataURL threw synchronously',
          method: 'getImgSrc',
          error,
          nonIndexedFields: {
            source,
            fileIndex: index,
            responseStatus: 'base64_conversion_failed_sync',
            fileName: imgArray[index]?.title,
          },
        })

        reject(error)
      }
    })
  }

  const closeFn = (event, reason, setOpen) => {
    if (reason === 'clickaway') {
      return
    }

    setOpen(false)
  }

  function renderSnackbar(
    open,
    setOpen,
    message,
    dontHide,
    noAction,
    severity,
  ) {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open}
        autoHideDuration={dontHide ? null : 3000}
        onClose={(e, reason) => closeFn(e, reason, setOpen)}
      >
        <Alert
          onClose={(e, reason) => closeFn(e, reason, setOpen)}
          severity={severity || 'warning'}
          sx={{ width: '100%' }}
          data-testid="file-upload-alert"
          action={noAction ? '' : undefined}
        >
          {message}
        </Alert>
      </Snackbar>
    )
  }

  function getImageUploadStatus() {
    const imageUploadedCount = hasUploadErrorImages
      ? `${totalImages - hasUploadErrorImages}/${totalImages}`
      : totalImages

    return uploaded === 'loaded' || uploaded === 'error' ? (
      <Grid item xs={12} md={12}>
        <Typography
          variant="h6"
          data-testid="subheader"
          className={classes.typography}
        >
          {`${imageUploadedCount} Image${
            totalImages > 1 ? 's' : ''
          } uploaded successfully`}
        </Typography>
      </Grid>
    ) : null
  }

  return woDetailFetched === true ? (
    <>
      <WorkOrderlanding />
      <Container>
        <Grid container rowSpacing={1} className={classes.container}>
          <Grid container columnSpacing={2} rowSpacing={1} alignItems="center">
            <Grid item>
              <AddPhotoAlternateIcon className={classes.addFirstIcon} />
            </Grid>
            <Grid item>
              <Typography variant="body1" component="span">
                {HOW_TO_UPLOAD_IMAGES}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" align="justify">
              {`Please click the Select Images button to add images to Work Order `}
              <strong>{`${woNumber}`}</strong>
              {`. You may select up to `}
              <strong>5</strong>
              {` images at a time, but you can select more after uploading. Please remain on the page while the images are uploading.`}
            </Typography>
          </Grid>
          <Grid item className={classes.uploadButtonDiv}>
            <>
              {((imagesToUpload?.length > 0 && uploaded === 'loaded') ||
                ((uploaded === false || uploaded === 'error') &&
                  imagesToUpload?.length < 5)) && (
                <>
                  <Button
                    startIcon={
                      <AddAPhotoIcon className={classes.iconFontSize} />
                    }
                    onClick={onCameraClick}
                    data-testid="camera-button"
                  >
                    Take Photo
                  </Button>
                  <Button
                    startIcon={
                      <AddPhotoAlternateIcon className={classes.iconFontSize} />
                    }
                    onClick={onUploadPhotoClick}
                    data-testid="select-image-button"
                  >
                    {SELECT_IMAGES}
                  </Button>
                </>
              )}
              {imagesToUpload?.length > 0 &&
                uploaded !== 'start' &&
                uploaded !== 'loaded' && (
                  <Button
                    variant="contained"
                    onClick={uploadImagesToServer}
                    data-testid="upload-image-button"
                  >
                    {hasUploadErrorImages ? RETRY_UPLOAD : UPLOAD_IMAGES}
                  </Button>
                )}
            </>
          </Grid>
          {getImageUploadStatus()}
          {imagesToUpload && (
            <Grid item xs={12}>
              <WorkOrderImageList
                images={imagesToUpload}
                from={UPLOAD_IMAGES}
                canDelete
                onDelete={(deleteIndex) => {
                  logUploadPhotoEvent({
                    method: 'onDeleteUploadImage',
                    params: {
                      deleteIndex,
                    },
                    body: {
                      deletedImage: getFileMetadata(
                        imagesToUpload[deleteIndex],
                        deleteIndex,
                      ),
                    },
                    response: {
                      status: 'removed_from_upload_queue',
                    },
                    miscLogs: {
                      remainingImageCount: imagesToUpload.length - 1,
                    },
                  })
                  setImagesToUpload(
                    imagesToUpload.filter(
                      (val, index) => index !== deleteIndex,
                    ),
                  )
                }}
                onDescriptionChange={(newDescription, descUpdateIndex) => {
                  setImagesToUpload(
                    imagesToUpload.map((val, index) => ({
                      ...val,
                      description:
                        index === descUpdateIndex
                          ? newDescription
                          : val.description,
                    })),
                  )
                }}
              />
            </Grid>
          )}
        </Grid>
      </Container>
      <input
        type="file"
        accept="image/*"
        id="image-upload"
        data-testid="image-upload-input"
        ref={inputFile}
        className={classes.photoUploadInput}
        multiple
        onChange={(e) => handleFileUpload(e, 'gallery')}
      />
      <input
        type="file"
        accept="image/*"
        capture="environment"
        id="camera-upload"
        data-testid="camera-upload-input"
        ref={cameraInputFile}
        className={classes.photoUploadInput}
        onChange={(e) => handleFileUpload(e, 'camera')}
      />
      {renderSnackbar(
        fileCountError,
        setFileCountError,
        UPLOAD_IMAGE_COUNT_WARNING,
      )}
      {renderSnackbar(
        fileTypeError,
        setFileTypeError,
        UPLOAD_IMAGE_TYPE_WARNING,
      )}
      {renderSnackbar(
        uploaded === 'start',
        () => {},
        LOADING_MESSAGE,
        true,
        true,
      )}
      {renderSnackbar(
        uploadedSnackbar,
        setUploadedSnackbar,
        UPLOADED_SUCCESSFULLY_MESSAGE,
        '',
        '',
        'success',
      )}
      {renderSnackbar(
        errorSnackbar,
        setErrorSnackbar,
        UPLOADED_ERROR_MESSAGE,
        '',
        '',
        'error',
      )}
    </>
  ) : null
}
